import { Fragment, useEffect, useState, type ReactElement } from "react";

import { api, ApiError } from "../../api/client";
import type { AdminStats } from "../../api/types";
import { AgeStatsTable, EMPTY_DISTRIBUTION, type AgeStatsGroup } from "../AgeStatsTable";
import { SkeletonRows } from "../Skeleton";
import { StatsSummary } from "../StatsSummary";
import { StatusCountRows } from "../StatusCountRows";
import { TabPanel } from "../Tabs";

// The stats-table columns: overall, then each parent protocol (cumulative),
// then each substudy — the same breakdown as the participant hierarchy.
function ageGroups(stats: AdminStats): AgeStatsGroup[] {
  const groups: AgeStatsGroup[] = [
    { key: "overall", label: "All", distribution: stats.age_stats.overall },
  ];
  for (const p of stats.protocols) {
    groups.push({
      key: p.irb_study,
      label: p.label,
      distribution: stats.age_stats.by_protocol[p.irb_study] ?? EMPTY_DISTRIBUTION,
    });
  }
  for (const p of stats.protocols) {
    for (const s of p.substudies) {
      groups.push({
        key: s.slug,
        label: s.name,
        distribution: stats.age_stats.by_substudy[s.slug] ?? EMPTY_DISTRIBUTION,
      });
    }
  }
  return groups;
}

// Aggregate study counts (GET /admin/stats) — aggregates only, no row-level
// PHI is rendered here.
export function OverviewTab(): ReactElement {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<AdminStats>("GET", "/admin/stats")
      .then(setStats)
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : "Could not load study counts."),
      );
  }, []);

  return (
    <TabPanel id="overview" title="Overview">
      {error ? (
        <p className="form-error">{error}</p>
      ) : !stats ? (
        <SkeletonRows rows={4} />
      ) : (
        <>
          <h3>Study Counts</h3>
          <table className="data-table breakdown">
            <tbody>
              <StatusCountRows
                label="Participants"
                total={stats.participants.total}
                byStatus={stats.participants.by_status}
              />
              <tr className="section">
                <th scope="row">Collaborators</th>
                <td className="num">{stats.collaborators.accounts}</td>
              </tr>
              <tr>
                <th scope="row" className="sub muted">
                  organizations
                </th>
                <td className="num">{stats.collaborators.organizations}</td>
              </tr>
              <StatusCountRows
                label="Photos"
                total={stats.photos.total}
                byStatus={stats.photos.by_status}
              />
            </tbody>
          </table>

          <h3>Participants by Protocol &amp; Substudy</h3>
          <p className="muted small">
            Parent protocol counts are cumulative: they include the substudies below.
          </p>
          <table className="data-table breakdown">
            <thead>
              <tr>
                <th scope="col">Protocol / Substudy</th>
                <th scope="col" className="num">
                  Participants
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.protocols.map((p) => (
                <Fragment key={p.irb_study}>
                  <tr className="section">
                    <th scope="row">{p.label}</th>
                    <td className="num">{p.participants}</td>
                  </tr>
                  {p.substudies.length === 0 ? (
                    <tr>
                      <th scope="row" className="sub muted">
                        no substudies
                      </th>
                      <td className="num muted">—</td>
                    </tr>
                  ) : (
                    p.substudies.map((s) => (
                      <tr key={s.slug}>
                        <th scope="row" className="sub muted">
                          {s.name}{" "}
                          <span className="small">
                            ({s.slug} ·{" "}
                            {s.origin === "aim_mak_lab"
                              ? "internal"
                              : (s.origin ?? "origin unset")}{" "}
                            ·{" "}
                            {s.contacts.length ? s.contacts.join(", ") : "no contact"})
                          </span>
                        </th>
                        <td className="num">{s.participants}</td>
                      </tr>
                    ))
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          <h3>FaceAge summary statistics</h3>
          <AgeStatsTable groups={ageGroups(stats)} />

          <StatsSummary demographics={stats.demographics} n={stats.demographics_n} />
        </>
      )}
    </TabPanel>
  );
}
